import * as React from "react";
import { Component } from "react";
import { AuthManager } from "../../component/auth/AuthContext";
import LabelizeApi, { LabelObsStudy, LabelTypedReport, LabelWorkflowBatch } from "../../api/LabelizeApi";
import { EXCLUDED_REASONS, Negative, NegativeRegion, Observation, Region, RegionType, Study } from "../../api/StudyApi";
import Viewer from "../../component/viewer/Viewer";
import GlmId from "../../component/GlmId";
import Moment from "react-moment";
import ArrayUtil from "../../util/ArrayUtil";
import MessageService from "../../service/MessageService";
import { Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import "./labelize.scss";
import LabelizeObsParam from "./LabelizeObsParam";
import { RouteComponentProps } from "react-router";
import LetterBadgeManager from "../../service/LetterBadgeManager";
import arr from "../../util/ArrayDecorator";
import LocalStorageService from "../../service/LocalStorageService";
import { isNullOrUndefined } from "../../util/LangUtil";
import { AnnotationToolMode } from "../../component/viewer/Viewport";
import { isRectangleTooSmall } from "../../util/RectangleUtility";
import Sentence from "../../component/nlp/Sentence";
import { DisplayMode } from "../../component/viewer/DisplayMode";
import { noStudyResult } from "../../component/viewer/AiDisplayPreferences";
import Autocomplete from "../../component/autocomplete/Autocomplete";
import ObsBadge from "../../component/observation/ObsBadge";
import { HARD_NEGATIVE_CONFIDENCE, NEGATIVE_CONFIDENCE, POSITIVE_CONFIDENCE } from "../../component/viewer/RegionMenu";
import i18n from "i18next";
import Tags from "../../component/Tags";
import ViewportImage from "../../component/viewer/ViewportImage";

interface LabelizeProps extends RouteComponentProps {
  authManager: AuthManager;
}

interface LabelizeState {
  study?: LabelObsStudy;
  nextStudy?: LabelObsStudy;
  waitingForNextStudy: boolean;
  viewportImages: Array<ViewportImage>;
  typedReports: Array<LabelTypedReport>;
  selectedObsIdx: number;
  lwb: LabelWorkflowBatch;
}

export default class Labelize extends Component<LabelizeProps, LabelizeState> {
  private labelizeApi: LabelizeApi;
  private observationTagService: LetterBadgeManager;
  private startTime: Date;

  constructor(props: LabelizeProps) {
    super(props);

    this.labelizeApi = LabelizeApi.with(props.authManager.cancelableClient());

    const params = new URLSearchParams(props.location.search);

    this.state = {
      viewportImages: [],
      waitingForNextStudy: false,
      typedReports: [],
      selectedObsIdx: 0,
      lwb: { labelBatchId: params.get("lbId"), labelWorkflowId: params.get("lwId") },
    };
    this.observationTagService = new LetterBadgeManager();
    this.startTime = null;
  }

  componentDidMount(): void {
    this.labelizeApi
      .getStudy(this.state.lwb)
      .then((study) => this.refreshScreenForStudy(study))
      .then((study) => this.loadNextStudy(study))
      .catch((err) => {
        if (err.response && err.response.status === 404) {
          this.returnToWelcomePage();
        } else {
          console.log(err);
          MessageService.error(i18n.t("error.occurred"));
        }
      });
  }

  componentWillUnmount(): void {
    this.labelizeApi.cancel();
  }

  private computeElapsedSec() {
    return Math.round((new Date().getTime() - this.startTime.getTime()) / 1000);
  }

  private getNextImagesToLoad(): Array<string> {
    const nextImagePaths: Array<string> = [];

    if (this.state.nextStudy) {
      this.state.nextStudy.series.forEach((s) => {
        s.images.forEach((i) => {
          nextImagePaths.push(i.path);
        });
      });
    }

    return nextImagePaths;
  }

  private loadNextStudy(current: LabelObsStudy): void {
    this.setState({ nextStudy: undefined }, () =>
      this.labelizeApi
        .getStudy(this.state.lwb, current)
        .then((nextStudy) => {
          if (this.state.waitingForNextStudy) {
            this.setState({ waitingForNextStudy: false });
            this.refreshScreenForStudy(nextStudy);
            this.loadNextStudy(nextStudy);
          } else {
            this.setState({ nextStudy });
          }
        })
        .catch((err) => {
          if (err.response && err.response.status === 404) {
            this.setState({ nextStudy: null });
          } else {
            console.log(err);
            MessageService.error(i18n.t("error.occurred"));
          }
        })
    );
  }

  public previousStudy(): void {
    const current = this.state.study;
    this.labelizeApi
      .previousStudy(this.state.study)
      .then((study) => {
        this.refreshScreenForStudy(study);
        this.setState({ nextStudy: current });
      })
      .catch((err) => {
        const httpStatus = err.response.status;
        if (httpStatus === 401) {
          MessageService.error(i18n.t("error.expiredPeriod"));

          this.setState({
            study: null,
            viewportImages: [],
            typedReports: [],
          });
        } else if (httpStatus === 404) {
          MessageService.info(i18n.t("error.previousStudy"));
        } else {
          console.log(err);
          MessageService.error(i18n.t("error.occurred"));
        }
      });
  }

  private refreshScreenForStudy(study: LabelObsStudy): LabelObsStudy {
    this.labelizeApi.getLabelTypeReportsByStudyId(study.id).then((typedReports) => this.setState({ typedReports }));
    this.refresh(study, []);
    return study;
  }

  private assignObsTags(study: Study | LabelObsStudy) {
    study.observations.forEach((obs, idx) => {
      obs._badge = this.observationTagService.getLetterBadge(obs.type, String(idx));
    });
  }

  private refresh(study: LabelObsStudy, prevImages = this.state.viewportImages): void {
    this.observationTagService.reset();
    this.assignObsTags(study);

    const viewportImages = ViewportImage.fromStudy(study, prevImages);

    // Select first obs by default
    let selectedObsIdx = this.state.selectedObsIdx;
    if (selectedObsIdx < 0 && study.observations.length + study.negatives.length > 0) {
      selectedObsIdx = 0;
    }

    this.setState({ study, viewportImages: viewportImages, selectedObsIdx });
  }

  private addAIRegionToNegative(region: Region) {
    if (this.state.study.showNegatives) {
      let negative = this.state.study.negatives.find((negative) => negative.type === region.obsType);

      const negRegion: NegativeRegion = {
        ...region,
        tags: [],
        confidence: HARD_NEGATIVE_CONFIDENCE,
      };

      const { study: currentStudy } = this.state;
      const updatedStudy = {
        ...currentStudy,
        negatives: [...currentStudy.negatives],
      };

      if (!negative) {
        negative = {
          type: region.obsType,
          regions: [],
        };

        updatedStudy.negatives.push(negative);
      }

      negative.regions.push(negRegion);

      this.refresh(updatedStudy);
    } else {
      MessageService.error(i18n.t("error.negativeVisible"));
    }
  }

  private saveRegion(region: Region, fromAiCopy = false) {
    if (isRectangleTooSmall(region)) {
      MessageService.error(i18n.t("error.boxTooSmall"));
      this.refresh(this.state.study);
      return;
    }

    if (isNullOrUndefined(this.state.selectedObsIdx)) {
      MessageService.error(i18n.t("error.noObs"));
      // refresh study to avoid unsaved region stays on the viewports
      this.refresh(this.state.study);
      return;
    }

    // Creation time
    if (isNullOrUndefined(region.obsIdx)) {
      region.obsIdx = this.state.selectedObsIdx;
    }

    if (isNullOrUndefined(region._type)) {
      if (this.state.selectedObsIdx < this.state.study.observations.length) {
        region._type = RegionType.OBSERVATION;
      } else {
        region._type = RegionType.NEGATIVE;
      }
    }

    // Default confidence value
    if (isNullOrUndefined(region.confidence)) {
      if (region._type === RegionType.NEGATIVE) {
        if (fromAiCopy) {
          region.confidence = HARD_NEGATIVE_CONFIDENCE;
        } else {
          region.confidence = NEGATIVE_CONFIDENCE;
        }
      } else {
        region.confidence = POSITIVE_CONFIDENCE;
      }
    }

    const obsArrayLength = this.state.study.observations.length;
    let regions: Region[];

    if (region._type === RegionType.OBSERVATION && region.obsIdx < obsArrayLength) {
      const obs = this.state.study.observations[region.obsIdx];
      regions = obs.regions;
    } else if (region._type === RegionType.NEGATIVE && region.obsIdx >= obsArrayLength) {
      const neg = this.state.study.negatives[region.obsIdx - this.state.study.observations.length];
      regions = neg.regions;
    } else {
      throw i18n.t("error.obsNotMatch", { type: region._type });
    }

    const regionIdx = regions.indexOf(region);
    if (regionIdx < 0) {
      regions.push(region);
    }

    this.refresh(this.state.study);
  }

  private deleteRegion(region: Region) {
    if (region && region.obsIdx > -1) {
      const study = this.state.study;

      if (region._type === RegionType.OBSERVATION) {
        const obs = study.observations[region.obsIdx];
        arr(obs.regions).remove((r) => r == region);
      } else if (region._type === RegionType.NEGATIVE) {
        const neg = study.negatives[region.obsIdx - study.observations.length];
        arr(neg.regions).remove((r) => r === region);
      } else {
        throw i18n.t("error.unknownRegionType", { type: region._type });
      }

      this.refresh(study);
    }
  }

  private selectObsOrNegative(idx: number): void {
    this.state.viewportImages.forEach((img) => {
      img.resetAttenuation();
      img.attenuateOtherRegions(idx);
    });

    this.setState({ selectedObsIdx: idx });
  }

  private createObservation(obsType: string): void {
    this.labelizeApi.newObservation(this.state.study, obsType).then((newObs) => {
      this.state.study.observations.push(newObs);
      const idx = this.state.study.observations.indexOf(newObs);
      this.refresh(this.state.study);
      this.selectObsOrNegative(idx);
    });
  }

  private createNegative(negType: string): void {
    if (arr(this.state.study.negatives).contains((n) => n.type === negType)) {
      MessageService.error(i18n.t("error.negativeExists", { type: negType }));
      return;
    }

    const negative: Negative = {
      type: negType,
      regions: [],
    };

    this.state.study.negatives.push(negative);

    this.refresh(this.state.study);
    const index = arr(this.state.study.negatives).findFirstIndex((n) => n === negative);
    this.selectObsOrNegative(this.translateIdxForNegative(index));
  }

  private deleteObservation(obs: Observation, evt?: React.MouseEvent<HTMLButtonElement>): void {
    if (evt) evt.stopPropagation();

    const idx = ArrayUtil.findFirstIndex(this.state.study.observations, (o) => {
      return o === obs;
    });

    this.observationTagService.unsetTagForObs(idx);

    arr(this.state.study.observations).remove((o) => o === obs);
    this.refresh(this.state.study);
  }

  private deleteNegative(negative: Negative) {
    const study = this.state.study;
    arr(study.negatives).remove((neg) => neg == negative);
    this.refresh(study);
  }

  private validate() {
    let isValid = true;

    this.state.study.observations.forEach((obs) => {
      if (obs.regions.length > 0) {
        obs.state = "validated";
        obs.excludedReason = null; // erase excludedReason if exist
      } else {
        MessageService.error(i18n.t("error.noRegion") + obs._badge.label);
        isValid = false;
      }
    });

    if (!isValid) return;

    this.saveAndNext();
  }

  private exclude(reason: string): void {
    this.state.study.observations.forEach((obs) => {
      obs.state = "excluded";
      obs.excludedReason = reason;
    });

    this.saveAndNext();
  }

  private doubt() {
    this.state.study.observations.forEach((obs) => {
      obs.state = "doubt";
    });

    this.saveAndNext(false);
  }

  private saveAndNext(checkAiActivated = true) {
    if (checkAiActivated && this.state.study.enablingAiWaitSecond && !this.state.study._aiHasBeenActivated) {
      MessageService.error(i18n.t("error.mustActivateAI"));
      return;
    }

    const current = this.state.study;
    const next = this.state.nextStudy;

    current.secDuration = this.computeElapsedSec();

    const save = this.labelizeApi.saveStudy(current).then((s) => {
      MessageService.info("Enregistré");
      this.startTime = null;
    });

    if (next === null) {
      save.then((_) => this.returnToWelcomePage());
    } else if (next === undefined) {
      save.then((_) => this.setState({ waitingForNextStudy: true }));
    } else {
      save.then((_) => this.refreshScreenForStudy(next)).then((_) => this.loadNextStudy(next));
    }
  }

  onCommentChange = ({ target: { value } }: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState(({ study }) => ({
      study: {
        ...study,
        comment: value,
      },
    }));
  };

  private negativeClass(idx: number): string {
    let cl = "observation cursor-pointer " + {};
    if (this.translateIdxForNegative(idx) === this.state.selectedObsIdx) {
      cl += " selected";
    }
    return cl;
  }

  private translateIdxForNegative(idx: number) {
    return this.state.study.observations.length + idx;
  }

  private returnToWelcomePage() {
    this.props.history.push("/labelize");
  }

  private aiActivated() {
    this.setState(({ study }) => {
      return {
        study: {
          ...study,
          _aiHasBeenActivated: true,
        },
      };
    });
  }

  private setImageTagsById(imageId: string, tags: Array<string>) {
    const study = this.state.study;
    study.series.forEach((s) =>
      s.images.forEach((img) => {
        if (img.id === imageId) {
          img.tags = tags;
        }
      })
    );
    this.refresh(study);
  }

  private onImageLoaded(): void {
    // start the time this first time an image is loaded in viewport
    if (this.startTime == null) {
      this.startTime = new Date();
    }
  }

  render() {
    return (
      <div className="labelize">
        {this.state.study && (
          <Viewer
            study={this.state.study}
            observations={this.state.study.observations}
            authManager={this.props.authManager}
            images={this.state.viewportImages}
            saveRegion={(r, fromCopy) => this.saveRegion(r, fromCopy)}
            deleteRegion={(r) => this.deleteRegion(r)}
            onImageTagsChanged={(imageId, tags) => this.setImageTagsById(imageId, tags)}
            imageTags={this.state.study.workflowImageTags?.sort()}
            aiDisplayPreferences={noStudyResult()}
            display={DisplayMode.labelize(this.state.study)}
            annotationToolMode={AnnotationToolMode.REGION}
            nextImagePaths={this.getNextImagesToLoad()}
            onAiRegionActivation={() => this.aiActivated()}
            onImageLoaded={() => this.onImageLoaded()}
            addAIRegionToNegative={(r) => this.addAIRegionToNegative(r)}
            actions={() => (
              <div className="list-inline">
                <div className="list-inline-item btn-group ">
                  <button className="btn btn-default" onClick={(evt) => this.previousStudy()}>
                    <i className="fa fa-long-arrow-left" />
                  </button>

                  {(this.props.authManager.isRole("LABEL") || this.props.authManager.hasRole("ADMIN")) && (
                    <Button onClick={() => this.doubt()} className="btn-group" title={i18n.t("labelize.questionned")}>
                      {i18n.t("labelize.doubt")}
                    </Button>
                  )}

                  {this.renderExcludedReasonsButton()}

                  <button className="btn btn-primary" onClick={(evt) => this.validate()}>
                    {i18n.t("global.validate")}
                  </button>

                  <button className="btn btn-default close-btn" onClick={(evt) => this.returnToWelcomePage()}>
                    {i18n.t("global.close")} <i className="fa fa-times" />
                  </button>
                </div>
              </div>
            )}
            aside={() => (
              <div>
                <div className="card">
                  <div className="card-title">{i18n.t("global.patient")}</div>
                  <div className="card-body">
                    <span>
                      {i18n.t("global.age")} : {this.state.study.patientAge} | {i18n.t("global.sex")} :{" "}
                      {this.state.study.patientSex}
                    </span>
                  </div>
                </div>
                {this.state.study.workflowStudyTags && this.state.study.workflowStudyTags.length > 0 && (
                  <div className="card">
                    <div className="card-title">Tags</div>
                    <div className="card-body">
                      <Tags
                        tags={this.state.study.tags}
                        availableTags={this.state.study.workflowStudyTags}
                        onTagsChanged={this.onStudyTagsChanged}
                      />
                    </div>
                  </div>
                )}
                <div className="card">
                  <div className="card-title">{i18n.t("global.observation.s")}</div>
                  <div className="card-body">
                    {this.state.study.observations.map((obs, idx) => {
                      const obsType = LocalStorageService.getObservationType(obs.type);

                      return (
                        <div
                          key={idx}
                          className={"label-obs-item" + (idx === this.state.selectedObsIdx ? " selected" : "")}
                          style={{ color: obs && obs._badge ? obs._badge.color : null }}
                          onClick={() => this.selectObsOrNegative(idx)}
                        >
                          <div className="row">
                            <div className="col">
                              <div className="pl-1">
                                {obsType.label}
                                <span className="pl-1" title={obsType.label}>
                                  {obs && obs._badge ? obs._badge.label : ""}
                                </span>
                              </div>
                            </div>
                            <div className="col text-right">
                              <button
                                className="btn btn-sm btn-outline-light"
                                onClick={(evt) => this.deleteObservation(obs, evt)}
                              >
                                <i className="fa fa-trash" />
                              </button>
                            </div>
                          </div>

                          <div>
                            {obs.params.map((param) => (
                              <div key={this.state.study.id + "-" + obs.id + "-" + param.type}>
                                <LabelizeObsParam
                                  param={param}
                                  paramType={LocalStorageService.getParamType(param.type)}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}

                    {this.renderCreateObsButton()}
                  </div>
                </div>

                {this.state.study.showNegatives && (
                  <div className="card">
                    <div className="card-title">{i18n.t("obs.negObs")}</div>

                    <div className="card-body">
                      {this.state.study.negatives &&
                        this.state.study.negatives.map((neg, idx) => (
                          <div
                            key={idx}
                            className={this.negativeClass(idx)}
                            onClick={() => this.selectObsOrNegative(this.translateIdxForNegative(idx))}
                          >
                            <div
                              className="observation-title"
                              style={{ color: LocalStorageService.getObservationType(neg.type).negativeColor }}
                            >
                              NO {neg.type.toUpperCase()}
                              <button
                                className="btn btn-secondary pull-right"
                                onClick={(evt) => this.deleteNegative(neg)}
                              >
                                <i className="fa fa-trash" />
                              </button>
                            </div>
                          </div>
                        ))}

                      {this.renderCreateNegativeButton()}
                    </div>
                  </div>
                )}

                <div className="card">
                  <div className="card-title">{i18n.t("global.typedReport")}</div>
                  <div className="card-body">
                    {this.state.typedReports &&
                      this.state.typedReports.map((report) => (
                        <div key={report.id}>
                          {report.sentences &&
                            report.sentences.map((sentence, si) => <Sentence key={si} value={sentence} />)}
                        </div>
                      ))}
                  </div>
                </div>

                <div className="card">
                  <div className="card-title">{i18n.t("global.comment")}</div>
                  <div className="card-body">
                    <textarea
                      className="form-control"
                      style={{ width: "100%" }}
                      rows={5}
                      value={this.state.study.comment || ""}
                      onChange={this.onCommentChange}
                    />
                  </div>
                </div>

                <div className="card">
                  <div className="card-title">{i18n.t("global.case")}</div>
                  <div className="card-body">
                    StudyId : <GlmId value={this.state.study.id} /> <br />
                    Indication : {this.state.study.indication}
                    <br />
                    StudyDate : <Moment format="DD/MM/YYYY HH:mm">{this.state.study.studyDate}</Moment> <br />
                  </div>
                </div>
              </div>
            )}
          />
        )}
      </div>
    );
  }

  private renderExcludedReasonsButton() {
    return (
      <UncontrolledDropdown className="btn-group">
        <DropdownToggle caret>{i18n.t("global.exclude")}</DropdownToggle>
        <DropdownMenu>
          {EXCLUDED_REASONS.filter((r) => !r.deprecated).map((r) => (
            <DropdownItem key={r.value} onClick={() => this.exclude(r.value)}>
              {r.text}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </UncontrolledDropdown>
    );
  }

  private renderCreateObsButton() {
    const obsTypes = this.state.study.labelingObsTypes.map((t) => LocalStorageService.getObservationType(t));

    return (
      <Autocomplete
        onChange={(obsType) => this.createObservation(obsType.code)}
        placeholder={i18n.t("obs.addObs")}
        items={obsTypes}
        toLabel={(ot) => ot.label}
        renderItem={(ot) => (
          <span>
            <ObsBadge key={ot.code} color={ot.color} text={ot.code} />
            {ot.label}
          </span>
        )}
      />
    );
  }

  private renderCreateNegativeButton() {
    const obsTypes = this.state.study.labelingObsTypes.map((t) => LocalStorageService.getObservationType(t));

    return (
      <Autocomplete
        onChange={(obsType) => this.createNegative(obsType.code)}
        placeholder={i18n.t("obs.addNeg")}
        items={obsTypes}
        toLabel={(ot) => ot.label}
        renderItem={(ot) => (
          <span>
            <ObsBadge key={ot.code} color={ot.color} text={ot.code} />
            {ot.label}
          </span>
        )}
      />
    );
  }

  private onStudyTagsChanged = (newTags: Array<string>) => {
    this.setState((prevState) => ({
      study: { ...prevState.study, tags: newTags },
    }));
  };
}
